/**
 * Tunable coefficients — the part of the model that is NOT public.
 *
 * The baselines in baselines.generated.ts are measured facts anyone can
 * reproduce from Cricsheet. This file is different: it is how player ratings
 * bend those base rates, and it is the proprietary part of the simulation.
 *
 * ## Shipping rules
 *
 * These values must NOT end up in the client bundle. At runtime the app fetches
 * a coefficient set from a Worker endpoint at match start and holds it in
 * memory only -- never localStorage, never a console log. The defaults here
 * exist so the calibration harness and unit tests have something to fit and
 * assert against; the build must strip or replace them.
 *
 * Verification for this is in the plan's §8: grep the production bundle for
 * these numbers and none may appear.
 *
 * ## These are placeholders until fitted
 *
 * The values below are structurally sensible starting points, not answers. The
 * calibration harness fits them so the engine reproduces real IPL. Hand-tuning
 * them to "feel right" is exactly the failure mode the harness exists to
 * prevent -- change them by refitting, not by intuition.
 *
 * Every result submitted to the server carries `version`, so the Worker can
 * replay a historical match with the coefficient set it was played under.
 */

export interface Coefficients {
  /** Bumped on every refit. Stored with each match so old results stay verifiable. */
  readonly version: number;

  /** Sensitivity of the survival contest to (bat - bowl). Higher = ratings matter more. */
  readonly kWicket: number;
  /** Sensitivity of four-hitting to (0.6*bat + 0.4*pow - bowl). */
  readonly kFour: number;
  /** Sensitivity of six-hitting to (pow - bowl). */
  readonly kSix: number;

  /** How strongly aggression raises boundary chance. */
  readonly aggressionBoundary: number;
  /** How strongly aggression raises wicket chance. Keeps risk attached to reward. */
  readonly aggressionWicket: number;

  /**
   * Chase pressure. Required rate above the phase par scales aggression
   * automatically, independent of the slider.
   */
  readonly chasePressure: number;
  /** Caution applied per wicket already lost, damping aggression. */
  readonly wicketCaution: number;

  /**
   * Scales how hard a CPU captain pushes at a strategic break when the chase
   * demands it -- `chooseAggression` in autoplay.ts returns a shape, this sets
   * its size.
   *
   * It exists because that function's thresholds were written by hand and
   * never fitted: until the `Yielded.events` wire was repaired it never ran at
   * all, so nothing measured it. Turned on at full strength it pushed death
   * overs to 10.49 an over against a real 10.06, and the fitter could only
   * answer by lowering scoring everywhere -- which cost the first innings a run
   * it could not spare. This lets the search size the chase response directly
   * instead of paying for it out of the first innings.
   */
  readonly chaseAggressionScale: number;

  /** Per-innings form spread. Larger = more "bad day at the office". */
  readonly formSpread: number;
  /**
   * Cap winners get a raised form *floor*, not a stat boost. Their expected
   * output barely moves; their downside shrinks. This is the honest reading of
   * "proven performer" and it cannot be exploited into absurd scores.
   */
  readonly capFormFloor: number;
  /** Small edge in the phase a cap was earned (death batting / death bowling). */
  readonly capSpecialtyEdge: number;

  /** Wide and no-ball rates scale inversely with bowler skill. */
  readonly extrasSkillSensitivity: number;

  /**
   * Intrinsic team intent per phase, before the captain's slider.
   *
   * The design calls for sides to attack in the first six overs and the last
   * five, and consolidate through the middle. That is genuine behaviour, not
   * something the per-ball baselines capture: the baselines describe what
   * happens on an average delivery in a phase, but by the death the batters at
   * the crease are lower-order players facing the best bowlers, so reproducing
   * a 10.06 run rate requires them to accept real risk.
   *
   * Raises boundary and wicket chance together, exactly like the slider.
   */
  readonly phaseIntent: {
    readonly POWERPLAY: number;
    readonly MIDDLE: number;
    readonly DEATH: number;
  };
}

/**
 * The coefficient set the client is currently expected to run.
 *
 * Just the number -- safe to ship, and the client needs it to label analytics
 * events and to tell the Worker which set a result was played under. The values
 * themselves live in coefficients.tuned.ts and are server-held.
 *
 * Bump it in coefficients.tuned.ts when refitting; this must follow.
 */
export const CURRENT_COEFFICIENT_VERSION = 3;
